"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTokenStore, useUserSettingsStore } from "@/lib/zustand";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  getQuote,
  QuoteRequest,
  Token,
  StepToolDetails,
  Process,
  ChainType,
  ChainId,
} from "@lifi/sdk";
import { type Address, formatUnits, parseUnits, zeroAddress } from "viem";
import { ArrowUpDownIcon } from "lucide-react";
import { TokenSwapCard } from "@/components/cards/token-swap-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useSwapAccount } from "@/hooks/use-swap-account";
import { Input } from "@/components/ui/input";
import { useBalance, useAccount as useWagmiAccount } from "wagmi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSolanaBalance, isSolanaAddress } from "@/lib/solana";
import { fetchSolanaTokenPrice } from "@/lib/prices";
import { useAutoSwitchChain } from "@/hooks/use-auto-switch-chain";
import { SWAP_FEE } from "@/config";
import { useSwapDisabled } from "@/hooks/swap-tokens/use-swap-disabled";
import { useHandleSwap } from "@/hooks/swap-tokens/use-handle-swap";
import { getChainById } from "@/constants/get-chain";
import { PurchaseWithCreditCard } from "@/components/modals/purchase-with-credit-card";
import { useProfileBalance } from "@getpara/react-sdk";

const crossChainSwapSchema = z.object({
  fromToken: z.object({
    chainId: z.number(),
    address: z.string(),
    decimals: z.number(),
    name: z.string(),
    symbol: z.string(),
    logoURI: z.string(),
    priceUSD: z.string(),
  }) satisfies z.ZodType<Token>,
  toToken: z.object({
    chainId: z.number(),
    address: z.string(),
    decimals: z.number(),
    name: z.string(),
    symbol: z.string(),
    logoURI: z.string(),
    priceUSD: z.string(),
  }) satisfies z.ZodType<Token>,
  fromChain: z.number(),
  toChain: z.number(),
  amount: z.string(),
  fromAddress: z.string().optional(),
  toAddress: z.string().optional(),
  slippage: z.number().optional(),
});

// Type for quote errors with optional DEX fallback link
type QuoteErrorInfo = {
  message: string;
  dexLink?: {
    url: string;
    label: string;
  };
} | null;

// LiFi's Solana chain ID
const SOLANA_LIFI_CHAIN_ID = 1151111081099710;

// Helper: detect if EVM token is unroutable (tax tokens, unsupported, etc.)
// More inclusive than just tax detection - any 404/no-route on EVM likely means unroutable
function isUnroutableEvmToken(error: unknown, chainId: number | string): boolean {
  // Only apply to EVM chains (not Solana)
  const isEvmChain = chainId !== SOLANA_LIFI_CHAIN_ID && chainId !== ChainId.SOL;
  if (!isEvmChain) return false;

  const errObj = error as Record<string, unknown>;
  const message = String(errObj?.message || "").toLowerCase();
  const code = errObj?.code;

  // Check for LiFi's no-route indicators
  return (
    code === 1006 ||
    code === "1006" ||
    message.includes("notfounderror") ||
    message.includes("request failed with status code 404") ||
    message.includes("no available quotes") ||
    message.includes("no routes found") ||
    message.includes("tax") ||
    message.includes("transfer tax")
  );
}

// Helper: get native DEX link for a chain
function getDexFallbackLink(
  chainId: number,
  tokenAddress: string
): { url: string; label: string } | null {
  // Normalize token address (some tokens use different case)
  const normalizedAddress = tokenAddress;

  switch (chainId) {
    case 56: // BSC
      return {
        url: `https://pancakeswap.finance/swap?outputCurrency=${normalizedAddress}`,
        label: "Swap on PancakeSwap",
      };
    case 1: // Ethereum
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${normalizedAddress}`,
        label: "Swap on Uniswap",
      };
    case 8453: // Base
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${normalizedAddress}&chain=base`,
        label: "Swap on Uniswap (Base)",
      };
    case 42161: // Arbitrum
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${normalizedAddress}&chain=arbitrum`,
        label: "Swap on Uniswap (Arbitrum)",
      };
    case 137: // Polygon
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${normalizedAddress}&chain=polygon`,
        label: "Swap on Uniswap (Polygon)",
      };
    case 10: // Optimism
      return {
        url: `https://app.uniswap.org/swap?outputCurrency=${normalizedAddress}&chain=optimism`,
        label: "Swap on Uniswap (Optimism)",
      };
    default:
      return null;
  }
}

export const SwapTokensForm = () => {
  const { address, caipAddress, isConnected, walletType } = useSwapAccount();
  const { address: wagmiAddress } = useWagmiAccount();
  const { userSettings } = useUserSettingsStore();
  const queryClient = useQueryClient();
  const { refetch: refreshBalance } = useProfileBalance();

  // Zustand store variables
  const {
    fromToken,
    setFromToken,
    toToken,
    setToToken,
    fromChain,
    setFromChain,
    toChain,
    setToChain,
    fromTokenAmount,
    setFromTokenAmount,
    toTokenAmount,
    setToTokenAmount,
    fromTokenAmountUSD,
    setFromTokenAmountUSD,
    toTokenAmountUSD,
    setToTokenAmountUSD,
    initializeSolPrice,
  } = useTokenStore();

  // Handle if we're on SVM or not
  const [isSVMtoEvm, setIsSVMtoEvm] = useState(false);
  const [isEvmToSVM, setIsEvmToSVM] = useState(false);
  const [evmAddress, setEvmAddress] = useState("" as Address);
  const [solanaAddress, setSolanaAddress] = useState("");
  const [isSwapDirection, setIsSwapDirection] = useState(false);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [isLoadingSwap, setIsLoadingSwap] = useState(false);
  const [recommendedRouteToolDetails, setRecommendedRouteToolDetails] =
    useState<StepToolDetails | null>(null);
  const [routeProcess, setRouteProcess] = useState<Process>();
  const [quoteError, setQuoteError] = useState<QuoteErrorInfo>(null);
  const [highPriceImpact, setHighPriceImpact] = useState<boolean>(false);

  // Auto-switch wallet/network when user selects input token (fromChain)
  useAutoSwitchChain();

  // Initialize SOL price on component mount
  useEffect(() => {
    initializeSolPrice();
  }, [initializeSolPrice]);

  // balances
  const { data: userEvmBalance } = useBalance({
    address: address as Address,
    token:
      fromToken.address !== zeroAddress && fromToken.address
        ? (fromToken.address as Address)
        : undefined, // undefined for native tokens
    chainId: fromChain.id,
    query: {
      select: (data) => data.value,
    },
  });

  const { data: solanaBalance, isLoading: isLoadingSolanaBalance } = useQuery({
    queryKey: ["solanaBalance", fromToken.address],
    queryFn: () =>
      getSolanaBalance(
        address as string,
        fromToken.address,
        fromToken.decimals
      ),
    enabled:
      !!address &&
      caipAddress?.includes("solana") &&
      fromToken.chainId === ChainId.SOL,
  });

  // Insufficient balance detection
  const insufficientBalanceInfo = useMemo(() => {
    // Don't show if no amount entered or amount is 0
    const enteredAmount = parseFloat(fromTokenAmount || "0");
    if (enteredAmount <= 0) return null;

    // Don't show while balance is loading
    const isEvm = fromChain.chainType === ChainType.EVM;
    const isSvm = fromChain.chainType === ChainType.SVM;

    if (isEvm) {
      // userEvmBalance is undefined while loading
      if (userEvmBalance === undefined) return null;

      const balanceFormatted = parseFloat(
        formatUnits(userEvmBalance, fromToken.decimals || 18)
      );
      if (enteredAmount > balanceFormatted) {
        return {
          balance: balanceFormatted.toFixed(4),
          symbol: fromToken.symbol,
        };
      }
    } else if (isSvm) {
      // solanaBalance is undefined while loading
      if (isLoadingSolanaBalance || solanaBalance === undefined || solanaBalance === null) return null;

      if (enteredAmount > solanaBalance) {
        return {
          balance: solanaBalance.toFixed(4),
          symbol: fromToken.symbol,
        };
      }
    }

    return null;
  }, [fromTokenAmount, fromChain.chainType, userEvmBalance, solanaBalance, isLoadingSolanaBalance, fromToken.decimals, fromToken.symbol]);

  // Form hook and schema
  const form = useForm<z.infer<typeof crossChainSwapSchema>>({
    resolver: zodResolver(crossChainSwapSchema),
    defaultValues: {
      fromToken: fromToken,
      toToken: toToken,
      fromChain: fromChain.id,
      toChain: toChain.id,
      amount: fromTokenAmount,
      fromAddress: address,
      slippage: Number(userSettings.slippage),
      toAddress: address,
    },
  });

  // Handle updating the swap direction
  const updateSwapDirection = () => {
    let tempToToken = fromToken;
    let tempFromToken = toToken;
    let tempFromTokenAmount = toTokenAmount;
    let tempToChain = fromChain;
    let tempFromChain = toChain;
    setToToken(tempToToken);
    setFromToken(tempFromToken);
    setFromTokenAmount(tempFromTokenAmount);
    setToTokenAmount("0");
    setToChain(tempToChain);
    setFromChain(tempFromChain);
    setIsSwapDirection(!isSwapDirection);
  };

  const swapDisabled = useSwapDisabled({ evmAddress, solanaAddress, isSVMtoEvm, isEvmToSVM, isLoadingRoutes, isLoadingSwap });
  const isSwapDisabled = swapDisabled.isSwapDisabled;
  const swapDisabledReason = swapDisabled.getSwapDisabledReason;
  const isCrossEcosystemToNonNative = useMemo(() => {
    return fromChain.chainType === ChainType.SVM &&
      toChain.chainType === ChainType.EVM &&
      toToken.address !== zeroAddress &&
      toToken.address !== "0x0000000000000000000000000000000000000000";
  }, [fromChain, toChain, toToken]);

  // Use ref to always have access to latest values without resetting debounce timer
  const quoteParamsRef = useRef({
    fromToken,
    toToken,
    fromTokenAmount,
    address,
    wagmiAddress,
    isSwapDisabled,
    evmAddress,
    solanaAddress,
    isSVMtoEvm,
    isEvmToSVM,
    fromChain,
    toChain,
    userSettings,
    walletType,
  });

  // Update ref on every render with latest values
  useEffect(() => {
    quoteParamsRef.current = {
      fromToken,
      toToken,
      fromTokenAmount,
      address,
      wagmiAddress,
      isSwapDisabled,
      evmAddress,
      solanaAddress,
      isSVMtoEvm,
      isEvmToSVM,
      fromChain,
      toChain,
      userSettings,
      walletType,
    };
  });

  // Stable debounced function that reads from ref - timer never resets
  const debouncedGetQuote = useMemo(() => {
    let timeoutId: NodeJS.Timeout;

    const debounced = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const params = quoteParamsRef.current;

        if (params.isSwapDisabled) {
          return;
        }

        if (
          params.fromToken.address === params.toToken.address &&
          params.fromToken.chainId === params.toToken.chainId
        ) {
          return;
        }

        // Validate toAddress based on chain type
        if (params.isSVMtoEvm) {
          if (
            !params.evmAddress ||
            !params.evmAddress.startsWith("0x") ||
            params.evmAddress.length !== 42
          ) {
            toast.error("Please enter a valid EVM address");
            return;
          }
        } else if (params.isEvmToSVM) {
          if (!params.solanaAddress || !isSolanaAddress(params.solanaAddress)) {
            toast.error("Please enter a valid Solana address");
            return;
          }
        } else if (!params.address) {
          toast.error("Please connect your wallet");
          return;
        }

        setIsLoadingRoutes(true);
        setQuoteError(null); // Clear previous error when starting new quote
        setHighPriceImpact(false); // Clear previous impact warning

        try {
          const formattedFromAmount = parseUnits(
            params.fromTokenAmount || "0",
            params.fromToken?.decimals || 18
          ).toString();

          console.log("[Quote Request] Amount calculation", {
            fromTokenAmount: params.fromTokenAmount,
            fromTokenDecimals: params.fromToken?.decimals,
            formattedFromAmount,
            fromTokenSymbol: params.fromToken?.symbol,
            toTokenSymbol: params.toToken?.symbol,
          });

          const toAddress = params.isSVMtoEvm
            ? params.evmAddress
            : params.isEvmToSVM
              ? params.solanaAddress
              : params.toChain.chainType === ChainType.SVM
                ? params.solanaAddress || params.address
                : params.address;

          // Use the correct address for the quote
          // For EVM: Use wagmi address if available (works for both Para internal and external)
          // For Solana: Use Para address
          const swapAddress =
            params.fromChain?.chainType === ChainType.EVM && params.wagmiAddress
              ? params.wagmiAddress
              : params.address;

          const quoteRequest: QuoteRequest = {
            fromChain: params.fromChain.id,
            toChain: params.toChain.id,
            fromToken: params.fromToken?.address,
            toToken: params.toToken?.address,
            fromAmount: formattedFromAmount,
            fromAddress: swapAddress || "",
            toAddress: toAddress,
            fee: SWAP_FEE,
            slippage: Number(params.userSettings.slippage),
            // Disable LiFi's default 10% price impact ceiling. LiFi's price feed for
            // low-cap tokens can be stale, causing inflated "impact" calculations that
            // block legitimate swaps. We surface the actual impact to the user below.
            maxPriceImpact: 1,
          };

          const quote = await getQuote(quoteRequest);

          if (!quote) {
            throw new Error("Failed to get quote after maximum retries");
          }

          setRecommendedRouteToolDetails(quote.toolDetails);

          const formattedToAmount = formatUnits(
            BigInt(quote.estimate.toAmount),
            params.toToken?.decimals || 18
          );

          setToTokenAmount(formattedToAmount);

          // LiFi's priceUSD can lag behind real market price for low-cap tokens.
          // Cross-check via DexScreener and use the more accurate value when there's
          // a meaningful discrepancy. The token amount is unaffected; this only corrects
          // the displayed USD value.

          // Cross-check FROM side (for low-cap tokens like KENJI being sold)
          let finalFromAmountUSD = quote.estimate.fromAmountUSD || "0";
          if (params.fromChain?.chainType === ChainType.SVM && params.fromToken?.address) {
            try {
              const dexScreenerPrice = await fetchSolanaTokenPrice(params.fromToken.address);
              if (dexScreenerPrice !== null && dexScreenerPrice > 0) {
                const fromAmountNumeric = Number(params.fromTokenAmount || 0);
                const dexScreenerUSD = fromAmountNumeric * dexScreenerPrice;
                const lifiUSD = Number(quote.estimate.fromAmountUSD || 0);

                if (lifiUSD > 0) {
                  const deviation = Math.abs(dexScreenerUSD - lifiUSD) / lifiUSD;
                  if (deviation > 0.10) {
                    finalFromAmountUSD = dexScreenerUSD.toFixed(2);
                  }
                } else if (dexScreenerUSD > 0) {
                  finalFromAmountUSD = dexScreenerUSD.toFixed(2);
                }
              }
            } catch {
              // DexScreener fetch failed - use LiFi's value as fallback
            }
          }
          setFromTokenAmountUSD(finalFromAmountUSD);

          // Cross-check TO side (for low-cap tokens like KENJI being bought)
          let finalToAmountUSD = quote.estimate.toAmountUSD || "0";
          if (params.toChain?.chainType === ChainType.SVM && params.toToken?.address) {
            try {
              const dexScreenerPrice = await fetchSolanaTokenPrice(params.toToken.address);

              if (dexScreenerPrice !== null && dexScreenerPrice > 0) {
                const toAmountNumeric = Number(formattedToAmount);
                const dexScreenerUSD = toAmountNumeric * dexScreenerPrice;
                const lifiUSD = Number(quote.estimate.toAmountUSD || 0);

                if (lifiUSD > 0) {
                  const deviation = Math.abs(dexScreenerUSD - lifiUSD) / lifiUSD;

                  if (deviation > 0.10) {
                    finalToAmountUSD = dexScreenerUSD.toFixed(2);
                  }
                } else if (dexScreenerUSD > 0) {
                  finalToAmountUSD = dexScreenerUSD.toFixed(2);
                }
              }
            } catch {
              // DexScreener fetch failed - use LiFi's value as fallback
            }
          }

          // Cap displayed receive USD at input minus fees. Use the corrected from-side USD
          // (already displayed on PAY card) for the cap. LiFi's raw fromAmountUSD can be stale
          // for low-cap tokens, which would cause this cap to artificially shrink the receive display.
          const fromUSDForCap = Number(finalFromAmountUSD);
          if (fromUSDForCap > 0) {
            // SWAP_FEE is 0.0125 (1.25%), add 0.5% buffer for slippage = 1.75% total
            const maxRealisticUSD = fromUSDForCap * 0.9825;
            const currentToUSD = Number(finalToAmountUSD);
            if (currentToUSD > maxRealisticUSD) {
              finalToAmountUSD = maxRealisticUSD.toFixed(2);
            }
          }

          setToTokenAmountUSD(finalToAmountUSD);

          // Calculate real price impact using DexScreener-corrected values on both sides
          const fromUSD = Number(finalFromAmountUSD);
          const toUSD = Number(finalToAmountUSD);
          if (fromUSD > 0 && toUSD > 0) {
            const realImpact = ((fromUSD - toUSD) / fromUSD) * 100;
            // Show warning if >5% loss AND this isn't just fee overhead on small amounts
            // (fees can cause ~2-3% "impact" on small swaps, which is expected)
            setHighPriceImpact(realImpact > 5 && fromUSD > 5);
          } else {
            setHighPriceImpact(false);
          }

          toast.dismiss();
        } catch (error: unknown) {
          setRecommendedRouteToolDetails(null);
          setToTokenAmount("0");
          setToTokenAmountUSD("0");
          toast.dismiss();

          // Debug: capture full error structure to diagnose tax token detection
          const errObj = error as Record<string, unknown>;
          console.log("[Quote Error Debug]", {
            errorType: error?.constructor?.name,
            errorMessage: errObj?.message,
            errorCode: errObj?.code,
            errorStatus: errObj?.status,
            errorResponse: errObj?.response,
            errorResponseData: (errObj?.response as Record<string, unknown>)?.data,
            errorResponseMessage: ((errObj?.response as Record<string, unknown>)?.data as Record<string, unknown>)?.message,
            errorResponseErrors: ((errObj?.response as Record<string, unknown>)?.data as Record<string, unknown>)?.errors,
            fullError: JSON.stringify(error, Object.getOwnPropertyNames(error || {})),
          });

          // Determine error type and set appropriate user-facing message
          const isRouteNotFound =
            (error instanceof Error && (
              error.message.includes("NotFoundError") ||
              error.message.includes("No available quotes") ||
              error.message.includes("No routes found")
            )) ||
            (error as { status?: number })?.status === 404;

          if (isRouteNotFound) {
            const chainId = params.toChain?.id ?? 0;
            const isEvmUnroutable = isUnroutableEvmToken(error, chainId);

            if (isEvmUnroutable && params.toToken?.address && params.toChain?.id) {
              // EVM token can't be routed - likely tax token, low liquidity, or unsupported
              const dexLink = getDexFallbackLink(
                typeof params.toChain.id === "number" ? params.toChain.id : 0,
                params.toToken.address
              );
              setQuoteError({
                message: "This token can't be routed through our aggregator. This often happens with tokens that have transfer taxes or low liquidity.",
                dexLink: dexLink || undefined,
              });
              console.log("[Quote] EVM unroutable token:", {
                fromToken: params.fromToken?.symbol,
                toToken: params.toToken?.symbol,
                chainId: params.toChain?.id,
              });
            } else {
              // Solana or other chain - show slippage advice
              setQuoteError({
                message: "Couldn't find a swap route. Try increasing slippage to 3-5% in settings, or use Jupiter directly for low-liquidity tokens.",
              });
              console.log("[Quote] Route not found:", {
                fromToken: params.fromToken?.symbol,
                toToken: params.toToken?.symbol,
                amount: params.fromTokenAmount,
              });
            }
          } else {
            // Other unexpected errors
            setQuoteError({ message: "Quote unavailable. Please try again." });
            console.error("[Quote] Unexpected error:", error);
          }
        } finally {
          setIsLoadingRoutes(false);
        }
      }, 600);
    };

    debounced.cancel = () => clearTimeout(timeoutId);

    return debounced;
  }, []); // Empty deps - function is stable, reads from ref

  const { handleSwap } = useHandleSwap({
    isSwapDisabled,
    swapDisabledReason,
    userEvmBalance,
    solanaBalance,
    isSVMtoEvm,
    evmAddress,
    solanaAddress,
    isEvmToSVM,
    setRouteProcess,
    toToken,
    setIsLoadingSwap,
    refreshBalance,
    invalidateQueries: () => void queryClient.invalidateQueries(),
  });

  useEffect(() => {
    // If the fromToken is Solana and the toToken is EVM, we ask for the EVM address
    if (
      isConnected &&
      caipAddress &&
      caipAddress.includes("solana") &&
      fromToken.chainId === ChainId.SOL &&
      toToken.chainId !== ChainId.SOL
    ) {
      setIsSVMtoEvm(true);
      setIsEvmToSVM(false);
      // If we're connected but the caipAddress is EVM, we don't ask for the EVM address
    } else if (isConnected && caipAddress && caipAddress.includes("eip155")) {
      // If the to token is Solana and the from token is not, we will ask for the SOL address
      if (
        toToken.chainId === ChainId.SOL &&
        fromToken.chainId !== ChainId.SOL
      ) {
        setIsEvmToSVM(true);
      } else {
        setIsEvmToSVM(false);
      }
      // we therefore don't ask for the EVM address
      setIsSVMtoEvm(false);
      // If both the to and from token are Solana, we don't ask for the EVM address
    } else if (
      isConnected &&
      caipAddress &&
      caipAddress.includes("solana") &&
      fromToken.chainId === ChainId.SOL &&
      toToken.chainId === ChainId.SOL
    ) {
      setIsSVMtoEvm(false);
      setIsEvmToSVM(false);
    }
  }, [caipAddress, isConnected, fromToken, toToken]);

  useEffect(() => {
    debouncedGetQuote();
    return () => debouncedGetQuote.cancel();
  }, [
    debouncedGetQuote,
    fromTokenAmount,
    toToken,
    fromToken,
    address,
    evmAddress,
    isSVMtoEvm,
    isEvmToSVM,
    solanaAddress,
    fromChain,
    toChain,
  ]);

  useEffect(() => {
    if (fromTokenAmount === "0" || fromTokenAmount === "") {
      setToTokenAmount("0");
      setToTokenAmountUSD("0");
    }
  }, [
    fromTokenAmount,
    toTokenAmount,
    toTokenAmountUSD,
  ]);

  const currentChain = getChainById(fromChain.id);

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSwap)}
          className="space-y-5"
        >
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TokenSwapCard
                    title="Pay"
                    amount={fromTokenAmount}
                    usdValue={Number(fromTokenAmountUSD)}
                    onAmountChange={(value) => {
                      field.onChange(value);
                      setFromTokenAmount(value);
                    }}
                    disabled={!isConnected}
                    isLoading={isLoadingRoutes}
                    type="send"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Insufficient balance warning */}
          {insufficientBalanceInfo && (
            <p className="text-xs text-yellow-400 text-center -mt-2">
              Insufficient balance — you have {insufficientBalanceInfo.balance} {insufficientBalanceInfo.symbol}
            </p>
          )}

          {/* Quote error warning - only show if not already showing insufficient balance */}
          {quoteError && !insufficientBalanceInfo && !isLoadingRoutes && (
            <div className="text-xs text-yellow-400 text-center -mt-2 space-y-1">
              <p>{quoteError.message}</p>
              {quoteError.dexLink && (
                <a
                  href={quoteError.dexLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-[#f9cd4f] underline hover:text-white transition-colors"
                >
                  {quoteError.dexLink.label} →
                </a>
              )}
            </div>
          )}

          {/* High price impact warning - only show when we have a valid quote with high impact */}
          {highPriceImpact && !quoteError && !insufficientBalanceInfo && !isLoadingRoutes && (
            <p className="text-xs text-yellow-400 text-center -mt-2">
              ⚠️ High price impact: this swap may result in significant loss due to low liquidity.
            </p>
          )}

          {/* Swap Direction Button */}
          <div className="my-1">
            <button
              type="button"
              onClick={() => updateSwapDirection()}
              className={cn(
                "group flex justify-center items-center mx-auto w-10 h-10 rounded-[10px] border border-[rgba(255,255,255,0.1)] bg-[#1a1a1a] shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:border-[rgba(249,205,79,0.3)] hover:bg-[rgba(255,255,255,0.08)] hover:shadow-[0_0_20px_rgba(254,154,0,0.3)] transition-all duration-200 ease-out",
                isSwapDirection
                  ? "rotate-180"
                  : ""
              )}
            >
              <ArrowUpDownIcon className="w-5 h-5 text-[#f9cd4f] transition-transform duration-200 ease-out group-hover:scale-105 group-hover:rotate-180" />
            </button>
          </div>
          <TokenSwapCard
            title="Receive"
            amount={toTokenAmount}
            usdValue={Number(toTokenAmountUSD)}
            onAmountChange={() => { }}
            isLoading={isLoadingRoutes}
            disabled={true}
            type="receive"
          />

          {isSVMtoEvm && (
            <div className="w-full space-y-2">
              <Input
                onChange={(e) => setEvmAddress(e.target.value as Address)}
                placeholder="0x123...."
                max={42}
                className="w-full text-center"
              />
              <p className="text-xs text-foreground text-center">
                Enter a valid EVM address you want to receive the tokens on.
              </p>
            </div>
          )}
          {isEvmToSVM && (
            <div className="w-full space-y-2">
              <Input
                onChange={(e) =>
                  isSolanaAddress(e.target.value as Address)
                    ? setSolanaAddress(e.target.value as Address)
                    : toast.error("Please enter a valid Solana address")
                }
                placeholder="2y1F3...."
                min={32}
                max={44}
                className="w-full text-center"
              />
              <p className="text-xs text-foreground text-center">
                Enter a valid Solana address you want to receive the tokens on.
              </p>
            </div>
          )}
          <Button
            type="submit"
            className="w-full rounded-[12px] flex items-center gap-2 text-[15px] font-bold bg-[linear-gradient(135deg,#f9cd4f_0%,#d4a012_100%)] text-[#111] shadow-[0_0_24px_rgba(249,205,79,0.2),0_4px_12px_rgba(0,0,0,0.3)] hover:shadow-[0_0_30px_rgba(249,205,79,0.4),0_4px_12px_rgba(0,0,0,0.3)] transition-all duration-200 ease-out"
            disabled={isSwapDisabled}
          >
            {parseFloat(fromTokenAmount) &&
              recommendedRouteToolDetails &&
              !isSwapDisabled ? (
              <>
                <Avatar className="size-6">
                  <AvatarImage src={recommendedRouteToolDetails?.logoURI} />
                  <AvatarFallback>
                    {recommendedRouteToolDetails?.name}
                  </AvatarFallback>
                </Avatar>
                Swap via {recommendedRouteToolDetails?.name}
              </>
            ) : (
              swapDisabledReason || "Swap Tokens"
            )}
          </Button>
          {isCrossEcosystemToNonNative && (
            <p className="text-xs text-yellow-400 text-center mt-2">
              ⚠️ Bridging from Solana to EVM tokens may deliver the native gas token (ETH/BNB) instead of the selected token. For best results, bridge to ETH first then swap.
            </p>
          )}
          {fromChain.id !== toChain.id &&
            toToken.address !== zeroAddress &&
            toToken.address !== "11111111111111111111111111111111" &&
            toToken.address !== "So11111111111111111111111111111111111111112" && (
              <p className="text-xs text-yellow-400 text-center mt-2">
                ⚠️ Cross-chain swaps involve a bridge + destination swap. In rare
                cases if the destination swap fails after bridging, you may
                receive the native token of the destination chain instead of{" "}
                {toToken.symbol}.
              </p>
            )}
          <PurchaseWithCreditCard />
        </form>
      </Form>
      {routeProcess && (
        <div className="mt-4 max-w-[400px] mx-auto flex flex-col gap-4">
          <p className="text-sm text-foreground text-center">
            {fromChain.id !== toChain.id
              ? "Bridge initiated! Cross-chain transfers can take 5-20 minutes. Check the explorer link below for status."
              : "Swap complete! Check the explorer link below for confirmation."}
          </p>
          <code className="text-xs max-w-2xl break-all bg-card p-2 rounded-md text-foreground">
            Transaction Hash: <a className="text-blue-500 hover:underline" href={`${currentChain.blockExplorers?.default.url}/tx/${routeProcess.txHash}`} target="_blank" rel="noopener noreferrer">View on {currentChain.name} Explorer</a>
          </code>
          {routeProcess.doneAt && !isNaN(new Date(routeProcess.doneAt as number).getTime()) && (
            <code className="text-xs max-w-2xl break-all bg-card p-2 rounded-md text-foreground">
              Completed:{" "}
              {new Date(routeProcess.doneAt as number).toLocaleString()}
            </code>
          )}
        </div>
      )}
    </>
  );
};
